import { useRef, useState } from "react";
import { UploadCloud, Download, CheckCircle2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { act } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import { ColumnMapper } from "./ColumnMapper";
import { PreviewTable } from "./PreviewTable";

const STEPS = ["Upload file", "Preview & check", "Import"];

export async function downloadTemplate(type, format) {
  const r = await act(() => api.get(`/imports/templates/${type}?format=${format}`, { responseType: "blob" }));
  if (!r) return;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r);
  a.download = `pressops-${type}-template.${format}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Stepper({ step }) {
  return (
    <ol className="flex flex-wrap gap-2" data-testid="import-stepper">
      {STEPS.map((s, i) => (
        <li key={s} data-testid={`import-step-${i + 1}`} className={cn("flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
          i + 1 === step ? "border-[#13294B] bg-[#13294B] text-white" : i + 1 < step ? "border-teal-200 bg-teal-50 text-teal-800" : "border-slate-200 text-slate-500")}>
          <span className="tabular-nums">{i + 1}</span>{s}
        </li>))}
    </ol>
  );
}

function FilePick({ onFile, label, testId, busy, variant = "default" }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden" data-testid={`${testId}-input`}
        onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onFile(f); }} />
      <Button type="button" variant={variant} disabled={busy} data-testid={testId} onClick={() => ref.current?.click()}
        className={variant === "default" ? "bg-[#13294B] hover:bg-[#1c3a66]" : ""}>
        <UploadCloud className="mr-1 h-4 w-4" />{busy ? "Reading file…" : label}
      </Button>
    </>
  );
}

function Summary({ run, onAgain }) {
  return (
    <div className="space-y-4" data-testid="import-summary">
      <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-semibold">Import finished</span></div>
      <div className="grid gap-3 sm:grid-cols-3">
        {[["Rows imported", run.imported, "summary-imported"], ["Rows skipped", run.skipped, "summary-skipped"], ["Errors", run.errors.length, "summary-errors"]].map(([l, v, id]) => (
          <div key={id} className="po-card p-4"><div className="po-label">{l}</div><div className="mt-1 text-2xl font-semibold tabular-nums" data-testid={id}>{v}</div></div>))}
      </div>
      {[...run.errors.map((e) => ({ ...e, kind: "error" })), ...run.warnings.map((w) => ({ ...w, kind: "warn" }))].length > 0 && (
        <ul className="max-h-56 space-y-1 overflow-auto text-sm" data-testid="summary-issues">
          {run.errors.map((e) => <li key={`e${e.row}`} className="text-rose-700">Row {e.row}: {e.message}</li>)}
          {run.warnings.map((w) => <li key={`w${w.row}`} className="text-amber-700">Row {w.row}: {w.message}</li>)}
        </ul>)}
      {onAgain && <Button variant="outline" data-testid="import-again-button" onClick={onAgain}><RotateCcw className="mr-1 h-4 w-4" />Import another file</Button>}
    </div>
  );
}

export function ImportWizard({ type, qbStep, onDone }) {
  const [session, setSession] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skip, setSkip] = useState(false);
  const [filter, setFilter] = useState("all");
  const step = run ? 3 : session ? 2 : 1;

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("type", type);
    if (qbStep) fd.append("qb_step", qbStep);
    fd.append("file", file);
    setBusy(true);
    const s = await act(() => api.post("/imports/sessions", fd));
    setBusy(false);
    if (s) { setSession(s); setSkip(false); setFilter(s.counts.error ? "error" : "all"); onDone?.(); }
  };
  const remap = async (mapping) => {
    const s = await act(() => api.put(`/imports/sessions/${session.id}/mapping`, { mapping }));
    if (s) setSession(s);
  };
  const commit = async () => {
    setBusy(true);
    const r = await act(() => api.post(`/imports/sessions/${session.id}/commit`, { skip_invalid: skip }), "Import complete");
    setBusy(false);
    if (r) { setRun(r); onDone?.(); }
  };
  const reset = () => { setSession(null); setRun(null); };
  const c = session?.counts;
  const blocked = !session || session.missing_required.length > 0 || c.valid === 0 || (c.error > 0 && !skip);

  return (
    <div className="space-y-5 p-5" data-testid="import-wizard">
      <Stepper step={step} />
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">Start from a template:</span>
            <Button size="sm" variant="outline" data-testid="template-csv-button" onClick={() => downloadTemplate(type, "csv")}><Download className="mr-1 h-4 w-4" />CSV</Button>
            <Button size="sm" variant="outline" data-testid="template-xlsx-button" onClick={() => downloadTemplate(type, "xlsx")}><Download className="mr-1 h-4 w-4" />Excel</Button>
          </div>
          <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
            <p className="mb-3 text-sm text-slate-500">Upload a .csv or .xlsx file (first sheet, header row first, up to 5,000 rows).</p>
            <FilePick onFile={upload} busy={busy} label="Choose file" testId="import-upload-button" />
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-5">
          <div className="text-sm text-slate-600">File <strong data-testid="import-filename">{session.filename}</strong> · <span data-testid="count-total">{c.total}</span> rows:{" "}
            <span className="text-emerald-700" data-testid="count-valid">{c.valid} ready</span>, <span className="text-rose-700" data-testid="count-error">{c.error} with errors</span>,{" "}
            <span className="text-amber-700" data-testid="count-skip">{c.skip} will be skipped</span></div>
          <ColumnMapper session={session} onChange={remap} />
          <div className="flex flex-wrap gap-2">{["all", "error", "skip", "valid"].map((f) => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} data-testid={`preview-filter-${f}`} onClick={() => setFilter(f)}
              className={filter === f ? "bg-[#13294B]" : ""}>{{ all: "All rows", error: "Errors", skip: "Skipped", valid: "Ready" }[f]}</Button>))}</div>
          <PreviewTable session={session} filter={filter} />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <FilePick onFile={upload} busy={busy} variant="outline" label="Re-upload fixed file" testId="import-reupload-button" />
              {c.error > 0 && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox data-testid="skip-invalid-checkbox" checked={skip} onCheckedChange={(v) => setSkip(!!v)} />Skip the {c.error} invalid row(s) and import the rest
                </label>)}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" data-testid="import-cancel-button" onClick={reset}>Start over</Button>
              <Button data-testid="import-commit-button" disabled={blocked || busy} onClick={commit} className="bg-[#389F95] hover:bg-[#2f877f]">
                {busy ? "Importing…" : `Import ${c.valid} row(s)`}</Button>
            </div>
          </div>
        </div>
      )}
      {step === 3 && <Summary run={run} onAgain={qbStep ? null : reset} />}
    </div>
  );
}
